import type { WatchStopHandle } from "vue"

import { uuidv7 } from "uuidv7"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertySchema, PropertyStatus, sharedWith } from "~/classes/entity"
import { FileUserRole } from "~/classes/file/properties/FileUserRole"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendSharedFolderDeleteRequests } from "~/classes/handlers/sharedFolder/delete"
import { sendSharedFolderMembershipRequest } from "~/classes/handlers/sharedFolder/membership"
import { sendSelfSharedFoldersRequest } from "~/classes/handlers/user/shared-folders"

interface State {
  /** Mapping from shared folder id to info */
  all: Record<string, {
    home: StaticFile
    archive: StaticFile
  }>
  new: {
    dialogOpen: boolean
    name: string
    handle: string
    loading: boolean
  }
  current: {
    invite: {
      requestId: string
      email: string
      isLoading: boolean
    }
  }
  sharedFolderWatcher?: WatchStopHandle
}

class SharedFolders extends GlobalComposable<State> {
  override defaultState = (): State => {
    return {
      all: {},
      new: {
        dialogOpen: false,
        name: "",
        handle: "",
        loading: false,
      },
      current: {
        invite: {
          requestId: "",
          email: "",
          isLoading: false,
        },
      },
    }
  }

  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    const { currentSharedFolderHome } = useNavigation()
    addInterfaceEventListener("sharedFolderMembershipResponse", this.onSharedFolderMembershipResponse)
    addInterfaceEventListener("selfSharedFoldersResponse", this.onSelfSharedFoldersResponse)

    this.state.value.sharedFolderWatcher = watchImmediate(currentSharedFolderHome, this.onUpdatedCurrentSharedFolder)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("sharedFolderMembershipResponse", this.onSharedFolderMembershipResponse)
    removeInterfaceEventListener("selfSharedFoldersResponse", this.onSelfSharedFoldersResponse)
    this.state.value.sharedFolderWatcher?.()
  }

  clearSharedFolderState = () => {
    this.state.value = this.defaultState()
  }

  resetSharedFolderCreationState = () => {
    this.state.value.new = {
      dialogOpen: false,
      name: "",
      handle: "",
      loading: false,
    }
  }

  resetSharedFolderInviteState = () => {
    this.state.value.current.invite = {
      requestId: "",
      email: "",
      isLoading: false,
    }
  }

  currentSharedFolderId = computed(() => useNavigation().currentSharedFolderHome.value?.rootSharedFolderId())
  currentSharedFolderInfo = computed(() => {
    const sharedFolderId = this.currentSharedFolderId.value
    if (isDefined(sharedFolderId))
      return this.state.value.all[sharedFolderId]
    return undefined
  })

  currentSharedFolderRole = computed(() => {
    const currentSharedFolderInfo = this.currentSharedFolderInfo.value
    if (!currentSharedFolderInfo)
      return undefined
    return currentSharedFolderInfo.home.impliedSelfRole()
  })

  requestSelfSharedFolders = () => {
    const request: SelfSharedFoldersRequestDatagram = {
      requestId: generateWorkerRequestId(),
      offline: false,
    }
    sendSelfSharedFoldersRequest(request)
    return request
  }

  onSelfSharedFoldersResponse = async (
    response: SelfSharedFoldersResponseDatagram,
  ) => {
    const { subscribeToFolderUpdates } = useFileSystem()
    const { addEntityEventListener } = useEvents()
    const { removeEntityEventListener } = useEvents()

    if (response.sharedFolders === undefined)
      return this.error("Shared folders listing errored")
    const subscribe: Set<string> = new Set()

    for (const [sharedFolderId, roots] of Object.entries(response.sharedFolders)) {
      if (!roots)
        throw new Error("Malformed message")
      const [home, archive] = roots.map(f => new StaticFile(f))
      if (this.state.value.all[sharedFolderId]) {
        this.state.value.all[sharedFolderId].home.mergeWithNewer(home)
        this.state.value.all[sharedFolderId].archive.mergeWithNewer(archive)
      }
      else {
        this.state.value.all[sharedFolderId] = { home, archive }
        addEntityEventListener(
          "fileProperties",
          home.id,
          this.onSharedFolderHomeProperties,
        )
        addEntityEventListener(
          "fileProperties",
          archive.id,
          this.onSharedFolderArchiveProperties,
        )
        addEntityEventListener(
          "sharedFolderDeleted",
          sharedFolderId,
          this.onDeletedSharedFolder,
        )
        subscribe.add(home.id)
        subscribe.add(archive.id)
      }
    }

    // if this is not offline, remove any folders we had added when cached that are not
    // in the final response
    for (const [sharedFolderId, roots] of Object.entries(this.state.value.all)) {
      if (!isDefined(response.sharedFolders[sharedFolderId])) {
        delete this.state.value.all[sharedFolderId]
        const { home, archive } = roots
        this.debug("Removing shared folder from state", home, archive)
        removeEntityEventListener("fileProperties", home.id, this.onSharedFolderHomeProperties)
        removeEntityEventListener("fileProperties", archive.id, this.onSharedFolderArchiveProperties)
        removeEntityEventListener("sharedFolderDeleted", sharedFolderId, this.onDeletedSharedFolder)
        subscribe.delete(home.id)
        subscribe.delete(archive.id)
      }
    }

    if (len(subscribe))
      subscribeToFolderUpdates([...subscribe])
  }

  /**
   * TODO: in order to make this "offline" proof (which perhaps we don't want to do?), we would need to
   * do this creation in two stages -- first, we create the shared folder with a random handle that we
   * can guarantee basically doesn't exist, then we modify that handle to the one the user desires.
   * Alternatively, we can re-create the shared folder with a different handle automatically the way
   * we move creation tasks to the archive.
   */
  createSharedFolder = async () => {
    const { state: myself } = useSelf()
    const { addEntityEventListener } = useEvents()
    const { createSharedFolderWithDefaultProperties } = useCache()
    if (!myself.value.user)
      throw new Error("Not logged in")

    this.state.value.new.loading = true

    const sharedFolderPreHash = StaticFile.sharedFolderPreHashId()
    const sharedFolderId = await StaticFile.idFromPrehash(sharedFolderPreHash)
    const homePreHash = StaticFile.clientSharedHomePreHashId()
    const archivePreHash = StaticFile.clientSharedArchivePreHashId()

    this.warn("Using the user-generated handle, we need to use a random handle or change the way we create shared folders")

    addEntityEventListener(
      "sharedFolderCreated",
      sharedFolderId,
      this.onSharedFolderCreated,
    )
    return await createSharedFolderWithDefaultProperties(
      sharedFolderPreHash,
      homePreHash,
      archivePreHash,
      myself.value.user.id,
      this.state.value.new.name,
      this.state.value.new.handle,
    )
  }

  onSharedFolderCreated = (event: SharedFolderEvent, offline: boolean) => {
    const { navigateToSharedFolder } = useNavigation()
    const { removeEntityEventListener } = useEvents()

    if (!offline)
      removeEntityEventListener("sharedFolderCreated", event.sharedFolderId, this.onSharedFolderCreated)

    this.resetSharedFolderCreationState()

    const sharedFolderId = event.sharedFolderId
    const home = new StaticFile(event.home)
    const archive = new StaticFile(event.archive)

    this.onAddedToSharedFolder({ sharedFolderId, home, archive })

    // at the end, lets navigate to the shared folder
    return navigateToSharedFolder(home)
  }

  onAddedToSharedFolder = ({ sharedFolderId, home, archive }: {
    sharedFolderId: string
    home: StaticFile
    archive: StaticFile
  }) => {
    const { addEntityEventListener } = useEvents()
    const { subscribeToFolderUpdates } = useFileSystem()

    if (this.state.value.all[sharedFolderId]) {
      this.state.value.all[sharedFolderId].home.mergeWithNewer(home)
      this.state.value.all[sharedFolderId].archive.mergeWithNewer(archive)
    }
    else {
      this.state.value.all[sharedFolderId] = { home, archive }
      addEntityEventListener(
        "fileProperties",
        home.id,
        this.onSharedFolderHomeProperties,
      )
      addEntityEventListener(
        "fileProperties",
        archive.id,
        this.onSharedFolderArchiveProperties,
      )
      addEntityEventListener(
        "sharedFolderDeleted",
        sharedFolderId,
        this.onDeletedSharedFolder,
      )
      subscribeToFolderUpdates([home.id, archive.id])
    }
  }

  onRemovedFromSharedFolder = (sharedFolderId: string) => {
    // TODO: unsubscribe from updates?

    const { removeEntityEventListener } = useEvents()
    const { navigateToSelfHome, currentFilePreview, currentRootFolder } = useNavigation()

    removeEntityEventListener("sharedFolderDeleted", sharedFolderId, this.onDeletedSharedFolder)

    if (this.state.value.all[sharedFolderId]) {
      const { home, archive } = this.state.value.all[sharedFolderId]
      removeEntityEventListener("fileProperties", home.id, this.onSharedFolderHomeProperties)
      removeEntityEventListener("fileProperties", archive.id, this.onSharedFolderArchiveProperties)
      delete this.state.value.all[sharedFolderId]
    }

    if (currentFilePreview.value?.rootSharedFolderId() === sharedFolderId)
      navigateToSelfHome()
    else if (currentRootFolder.value?.rootSharedFolderId() === sharedFolderId)
      navigateToSelfHome()
  }

  onSharedFolderHomeProperties = (event: FileEvent) => {
    const newHome = StaticFile.fromEvent(event)
    for (const [id, { home }] of Object.entries(this.state.value.all)) {
      if (home.id !== newHome.id)
        continue
      this.state.value.all[id]!.home.mergeWithNewer(newHome)
      return
    }
    this.error("Did not find shared folder home")
  }

  onSharedFolderArchiveProperties = (event: FileEvent) => {
    const newArchive = StaticFile.fromEvent(event)
    for (const [id, { archive }] of Object.entries(this.state.value.all)) {
      if (archive.id !== newArchive.id)
        continue
      this.state.value.all[id]!.archive.mergeWithNewer(newArchive)
      return
    }
    this.error("Did not find shared folder archive")
  }

  /**
   * When you navigate and the shared folder home changes. This watcher is attached
   * to the `currentSharedFolderHome` of the useNavigation state manager.
   */
  onUpdatedCurrentSharedFolder = (newSharedFolderHome?: ReactiveFile, oldSharedFolderHome?: ReactiveFile) => {
    const { fetchUserInfoIfNotCached } = useSavedUsers()

    if (newSharedFolderHome?.id === oldSharedFolderHome?.id)
      return

    // Use the data in the shared folder state as a source of truth
    // any user data we fetch we use the `useSavedUsers` composable
    // users that are

    if (!isDefined(newSharedFolderHome))
      return this.debug("Navigated out of shared folder", oldSharedFolderHome)

    const rootSharedFolderId = newSharedFolderHome.rootSharedFolderId()
    if (!isDefined(rootSharedFolderId))
      throw new Error("No way to get shared folder info without shared folder id")

    const newHome = this.state.value.all[rootSharedFolderId]?.home
    if (!newHome) {
      return this.error("No shared folder state found for id and fetching shared folder info not implemented yet", newHome)
    }

    const members = newHome.sharedWithUserRoles()
    if (len(members) === 0)
      return this.warn("No members in shared folder")

    fetchUserInfoIfNotCached(Object.keys(members))
  }

  /**
   * Inviting a user requires we know their user id. In order to retrieve that we must get the
   * user's id via a find request before we can actually do the inviting.
   */
  inviteUserToCurrentSharedFolder = async () => {
    const { state: myself } = useSelf()
    const { notifyError } = useAlerts()
    const { isOffline } = useCache()
    const { findUserIdByEmail } = useSavedUsers()

    // TODO: allow for offline changes?
    if (isOffline())
      return notifyError("Offline mode", "You cannot edit shared folder permissions in offline mode")

    const sharedFolderId = this.currentSharedFolderId.value
    const sharedFolderInfo = this.currentSharedFolderInfo.value

    const current = this.state.value.current
    if (!current || !myself.value.user)
      return console.error("No current shared folder to invite to")
    if (!isDefined(sharedFolderId) || !isDefined(sharedFolderInfo))
      return console.error("No shared folder info found for current shared folder")
    const email = current.invite.email.trim()
    if (!email)
      return console.error("No email provided for invite")

    current.invite.isLoading = true
    let userId
    try {
      userId = await findUserIdByEmail(email)
    }
    catch {
      current.invite.isLoading = false
      return notifyError("User not found", "User not found with that email address")
    }

    // Updating the membership requires we create a fake shared folder object that only has
    // the user membership as a single property of the home and archive directories.
    // Note that its possible this user was removed and therefore adding / inviting them
    // would require a version increment.
    const existing = sharedFolderInfo.home.property(sharedWith(userId))
    const timestamp = uuidv7()
    const property = {
      propertyId: sharedWith(userId),
      schema: PropertySchema.RoleUuidValue,
      version: existing?.version ? existing.version + 1 : 0,
      author: myself.value.user.id,
      value: {
        type: PropertySchema.RoleUuidValue,
        role: FileUserRole.newViewer().inner(),
      },
      timestamp,
      status: PropertyStatus.StatusPending,
      sharedWithUserId: userId,
    } as FileProperty

    const home = StaticFile.blank(sharedFolderInfo.home.id)
    home.addProperty({ ...property, id: home.id })
    home.setModifiedAt(timestamp, myself.value.user.id)

    const archive = StaticFile.blank(sharedFolderInfo.archive.id)
    archive.addProperty({ ...property, id: archive.id })
    archive.setModifiedAt(timestamp, myself.value.user.id)

    // This could be a lot cleaner...
    this.state.value.current.invite.requestId = generateWorkerRequestId()
    const request: SharedFolderMembershipRequestDatagram = {
      requestId: this.state.value.current.invite.requestId,
      sharedFolderId,
      home: home.inner(),
      archive: archive.inner(),
      offline: false,
    }
    sendSharedFolderMembershipRequest(request)
  }

  editCurrentSharedFolderMemberPermissions = ({ userId, role, remove }: {
    userId: string
    role?: FileUserRole
    remove?: boolean
  }) => {
    const { state: myself } = useSelf()
    const { notifyError } = useAlerts()
    const { isOffline } = useCache()

    // TODO: allow for offline changes?
    if (isOffline())
      return notifyError("Offline mode", "You cannot edit shared folder permissions in offline mode")

    // Check if we are updating our own permissions, and error if we are an owner
    if (myself.value.user?.id === userId && this.currentSharedFolderRole.value?.isOwner())
      return notifyError(
        "Cannot remove yourself as owner",
        "You cannot remove yourself as owner of a shared folder. Please contact another owner to do this.",
      )

    const sharedFolderId = this.currentSharedFolderId.value
    const sharedFolderInfo = this.currentSharedFolderInfo.value

    if (!isDefined(sharedFolderId) || !isDefined(sharedFolderInfo))
      throw new Error(`No shared folder state found for id: ${sharedFolderId}`)
    if (!myself.value.user)
      throw new Error("Not logged in")

    const property = clone(sharedFolderInfo.home.property(sharedWith(userId)))
    if (!isDefined(property))
      throw new Error("Cannot edit permissions for user that is not in shared folder")
    const timestamp = uuidv7()

    property.version = property.version + 1
    if (remove)
      property.deleted = true
    else if (role)
      property.value = { type: PropertySchema.RoleUuidValue, role: role.inner() }
    else
      throw new Error("No role provided for editing permissions")

    property.status = PropertyStatus.StatusPending
    property.timestamp = timestamp

    const home = StaticFile.blank(sharedFolderInfo.home.id)
    home.addProperty(property)
    home.setModifiedAt(timestamp, myself.value.user.id)

    const archive = StaticFile.blank(sharedFolderInfo.archive.id)
    archive.addProperty(property)
    archive.setModifiedAt(timestamp, myself.value.user.id)

    this.state.value.current.invite.requestId = generateWorkerRequestId()

    const request: SharedFolderMembershipRequestDatagram = {
      requestId: this.state.value.current.invite.requestId,
      sharedFolderId,
      home: home.inner(),
      archive: archive.inner(),
      offline: false,
    }

    sendSharedFolderMembershipRequest(request)

    return request
  }

  leaveSharedFolder = (sharedFolderId: string) => {
    const { state: myself } = useSelf()
    const { notifyError } = useAlerts()

    const sharedFolderInfo = this.state.value.all[sharedFolderId]

    if (!isDefined(sharedFolderInfo))
      throw new Error(`No shared folder state found for id: ${sharedFolderId}`)
    if (!myself.value.user)
      throw new Error("Not logged in")

    const members = sharedFolderInfo.home.sharedWithUserRoles()
    const owners = Object.values(members).map(m => m.role).filter(r => r.isOwner())
    const role = members[myself.value.user.id]?.role

    // Check if we are the only owner, and error if so (we must do a delete since we can't leave
    if (role?.isOwner() && owners.length <= 1)
      return notifyError(
        "Cannot leave as owner",
        "You cannot leave a shared folder since you are the only owner. Delete the shared folder or add an owner before leaving.",
      )

    const home = StaticFile.blank(sharedFolderInfo.home.id)
    const archive = StaticFile.blank(sharedFolderInfo.archive.id)

    const propertyId = sharedWith(myself.value.user.id)
    const property = clone(sharedFolderInfo.home.property(propertyId))
    const timestamp = uuidv7()

    if (!property)
      throw new Error("Shared folder info incomplete -- needs own membership")
    property.deleted = true
    property.status = PropertyStatus.StatusPending
    property.timestamp = timestamp

    home.addProperty(property)
    home.setModifiedAt(timestamp, myself.value.user.id)

    archive.addProperty(property)
    archive.setModifiedAt(timestamp, myself.value.user.id)

    const request: SharedFolderMembershipRequestDatagram = {
      requestId: generateWorkerRequestId(),
      sharedFolderId,
      home: home.inner(),
      archive: archive.inner(),
      offline: false,
    }

    sendSharedFolderMembershipRequest(request)

    this.onRemovedFromSharedFolder(sharedFolderId)

    return request
  }

  deleteSharedFolder = (sharedFolderId: string) => {
    const { state: myself } = useSelf()
    const { notifyError } = useAlerts()

    const sharedFolderInfo = this.state.value.all[sharedFolderId]

    if (!isDefined(sharedFolderInfo))
      throw new Error(`No shared folder state found for id: ${sharedFolderId}`)
    if (!myself.value.user)
      throw new Error("Not logged in")

    const members = sharedFolderInfo.home.sharedWithUserRoles()
    const role = members[myself.value.user.id]?.role

    // Check if we are the only owner, and error if so (we must do a delete since we can't leave)
    if (!role?.isOwner()) {
      notifyError(
        "Cannot delete this shared folder",
        "You cannot delete a shared folder unless you are an owner of it. Please contact an owner to delete it.",
      )
      return
    }

    const request: SharedFolderDeleteRequestDatagram = {
      requestId: generateWorkerRequestId(),
      sharedFolderId,
      offline: false,
    }

    sendSharedFolderDeleteRequests([request])

    this.onRemovedFromSharedFolder(sharedFolderId)

    return request
  }

  onDeletedSharedFolder = (event: SharedFolderEvent) => {
    this.onRemovedFromSharedFolder(event.sharedFolderId)
  }

  onSharedFolderMembershipResponse = (response: SharedFolderMembershipResponseDatagram) => {
    if (response.requestId === this.state.value.current?.invite.requestId)
      this.resetSharedFolderInviteState()

    if (response.error || !response.home || !response.archive)
      return console.warn("Can't update membership. request errored")

    const { sharedFolderId, home, archive } = response
    this.onSharedFolderInfo({
      sharedFolderId,
      home: new StaticFile(home),
      archive: new StaticFile(archive),
    })
  }

  onSharedFolderInfo = ({ sharedFolderId, home, archive }: {
    sharedFolderId: string
    home: StaticFile
    archive: StaticFile
  }) => {
    const { state: myself } = useSelf()

    if (!myself.value.user)
      return console.error("Received shared folder updated when not logged in")

    const sharedWithMe = home.property(sharedWith(myself.value.user.id))
    if (sharedWithMe?.deleted) {
      console.warn("You were removed from a shared space (or you left)", home, archive)
      this.onRemovedFromSharedFolder(sharedFolderId)
    }
    else if (!isDefined(this.state.value.all[sharedFolderId])) {
      // This update info object has all the information about the shared folder only
      // because for now I made the server request all properties. In the future, this
      // may not be the case!
      console.debug("Received invitation to a shared folder, subscribing to future updates", home, archive)
      this.onAddedToSharedFolder({ sharedFolderId, home, archive })
    }
    else {
      this.state.value.all[sharedFolderId].home.mergeWithNewer(home)
      this.state.value.all[sharedFolderId].archive.mergeWithNewer(archive)
    }
  }
}

const sharedFolders = new SharedFolders("shared-folders", theme.colors.violet.hex)
export default function () {
  return sharedFolders.use()
}
